import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  UrlSegment
} from '@angular/router';
import {filter, switchMap, tap} from 'rxjs/operators';
import {CallanOnlineRecognizeService} from "../../services/callan-online-recognize.service";
import {PersonPasswordChangeComponent} from "../../components/person-password-change/person-password-change.component";
import {PersonEmailChangeComponent} from "../../components/person-email-change/person-email-change.component";
import {ConfirmDialogService} from "../../services/confirm-dialog.service";
import {ToastNotification, ToastNotificationsService} from "../../services/toast-notifications.service";
import {FunctionalAccessService} from "../../services/functional-access.service";
import {combineLatest, EMPTY, Subscription} from "rxjs";
import {LearningUnitTeacherStatus, V3Teacher} from "../../model/v3/schools";
import {ManagerUtils} from "../../services/utils/v3/manager-utils";
import {TeacherV3RestService} from "../../services/rest/v3/teacher-rest.service";
import {VerifiedEmailChangeRequest, VerifiedPasswordChangeRequest} from "../../model/v3/people";
import {WorkflowsRestService} from "../../services/rest/v3/workflows-rest.service";
import {CasaRestApiV2} from "../../services/rest/casa-rest.service";
import {TransactionsV3RestService} from "../../services/rest/v3/transactions-rest.service";

import {ManagerSubscriptionsGuardService} from "../../services/col2/manager-subscriptions-guard.service";
@Component({
    selector: 'app-manager-teacher-details-layout',
    templateUrl: './manager-teacher-details-layout.component.html',
    styleUrls: ['./manager-teacher-details-layout.component.css'],
    standalone: false
})
export class ManagerTeacherDetailsLayoutComponent implements OnInit, OnDestroy {

  _schoolId: number;
  _teacherId: number;
  currentFragment: string;
  searchTerm = '';
  teacher: V3Teacher;
  private currentFragments: string[];
  @ViewChild('passwordModal', {static: true})
  passwordModal: PersonPasswordChangeComponent;
  @ViewChild('emailModal', {static: true})
  emailModal: PersonEmailChangeComponent;
  private dataSubscription: Subscription


  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private casa: CasaRestApiV2,
    public callanOnlineRecognize: CallanOnlineRecognizeService,
    private confirmationService: ConfirmDialogService,
    private toastNotificationService: ToastNotificationsService,
    public functionalAccessService: FunctionalAccessService,
    public subscriptionsGuard: ManagerSubscriptionsGuardService,
    private workflowsV3Rest: WorkflowsRestService,
    private teacherV3Rest: TeacherV3RestService,
    private transactionsV3Rest: TransactionsV3RestService,
  ) {
    router.events.pipe(
      filter((e: Event): e is NavigationStart | NavigationEnd | NavigationCancel | NavigationError => e instanceof NavigationStart ||
                                  e instanceof NavigationEnd ||
                                  e instanceof NavigationCancel ||
                                  e instanceof NavigationError))
    .subscribe((e: RouterEvent) => {
        this.currentFragments = e['urlAfterRedirects'] ? e['urlAfterRedirects'].split('/') : e.url.split('/');
        this.currentFragment = this.currentFragments[this.currentFragments.length - 1]
    });
  }

  changePasswordCallback = (request: VerifiedPasswordChangeRequest) => this.teacherV3Rest.changeTeacherPasswordInstantly(this._schoolId, this._teacherId, request)
  changeEmailCallback = (request: VerifiedEmailChangeRequest) => this.workflowsV3Rest.requestTeacherEmailChange(this._schoolId, this._teacherId, request)

  getPersonName() {
    if (!this.teacher) return '';
    return ManagerUtils.getName(this.teacher.person);
  }

  loadData() {
    if (!this._schoolId || !this._teacherId) {
      return;
    }

    this.loadTeacher().subscribe(
      _ => {
        const childActiveStateUrl = (this.activeRoute.children && this.activeRoute.children.length>0) ? this.activeRoute.children[0].snapshot.url : null;
        const selectedTeacherPage = this.getTeacherPageSelected(childActiveStateUrl);
        if (!selectedTeacherPage) {
          this.redirectToDefaultPage()
        }
      }
    )
  }

  loadTeacher(){
    return this.teacherV3Rest.getTeacher(this._schoolId, this._teacherId).pipe(
      tap(teacher => this.teacher = teacher)
    )
  }

  ngOnInit() {
    if(!this.currentFragment) this.determineActiveButton(this.router.url.split('/'));
    this.dataSubscription = combineLatest([
      this.activeRoute.parent.paramMap,
      this.activeRoute.paramMap
    ]).pipe(
      tap(([parentParams, params]) => {
          this._schoolId = Number(parentParams.get('schoolId'))
          this._teacherId = Number(params.get('teacherId'))
      })
    ).subscribe(_ => this.loadData())
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe()
  }

  private supportedPaths = ['lessons', 'profile',  'casa-profile', 'products', 'subscriptions', 'lessons-list', 'lessons-calendar'];


  determineActiveButton(currentFragments: string[]): void {
    this.currentFragment = currentFragments[currentFragments.length - 1]
  }

  isCallanOnline() {
    if (!this._schoolId) return false;
    return this.callanOnlineRecognize.isCallanOnlineFranchiseById(this._schoolId);
  }

  private getTeacherPageSelected(urls: UrlSegment[]) {
    if (!urls) return null;
    if (urls.length > 0) {
      const lastPath = urls[urls.length - 1].path;
      if (this.supportedPaths.indexOf(lastPath) >=0 ) return lastPath;
    }
    return null;
  }

  private redirectToDefaultPage() {
    if (!this._schoolId || !this._teacherId) return;
    if (this.isCallanOnline()) {
      this.router.navigate(["school", this._schoolId.toString(), "teacher", this._teacherId.toString(), "lessons"]).then();
    } else {
      this.router.navigate(["school", this._schoolId.toString(), "teacher", this._teacherId.toString(), "casa-profile"]).then();
    }
  }

  getStatus() {
    return this.teacher && this.teacher.status || '';
  }

  getAccountEmail() {
    return this.teacher && this.teacher.person.registrationEmail || '';
  }

  canActivate() {
    return this.teacher && this.teacher.status === LearningUnitTeacherStatus.BLOCKED;
  }

  isActive() {
    return this.teacher && this.teacher.status === LearningUnitTeacherStatus.ACTIVE;
  }

  activate() {
    this.confirmationService.doOnConfirm("Are you sure you want to activate the teacher?",
      () => {
        this.teacherV3Rest.changeTeacherStatus(this._schoolId, this._teacherId, LearningUnitTeacherStatus.ACTIVE).pipe(
          tap(_ => this.toastNotificationService.display(new ToastNotification("Teacher activation",`Teacher has been activated.`))),
          switchMap( _ => this.loadTeacher())
        ).subscribe();
      },"Teacher activation", "bg-warning");
  }

  block() {
    this.confirmationService.doOnConfirm("Are you sure you want to block the teacher?",
      () => {
        this.transactionsV3Rest.isTeacherSubscribed(this._schoolId, this._teacherId).pipe(
          switchMap(isSubscribed => {
            if (isSubscribed) {
              this.toastNotificationService.display(new ToastNotification("Teacher blocking", "Teacher has active books subscription. Cancel it before blocking."));
              return EMPTY;
            }
            return this.teacherV3Rest.changeTeacherStatus(this._schoolId, this._teacherId, LearningUnitTeacherStatus.BLOCKED);
          }),
          tap(_ => this.toastNotificationService.display(new ToastNotification("Teacher blocking",`Teacher has been blocked.`))),
          switchMap( _ => this.loadTeacher())
        ).subscribe();
      },"Teacher blocking", "bg-warning");
  }

  delete() {
    this.confirmationService.doOnConfirm("Are you sure you want delete the teacher?",
      () => {
        this.transactionsV3Rest.isTeacherSubscribed(this._schoolId, this._teacherId).pipe(
          switchMap(isSubscribed => {
            if (isSubscribed) {
              this.toastNotificationService.display(new ToastNotification("Teacher deletion", "Teacher has active books subscription. Cancel it before deletion."));
              return EMPTY;
            }
            return this.teacherV3Rest.deleteTeacher(this._schoolId, this._teacherId);
          }),
          tap(_ => this.toastNotificationService.display(new ToastNotification("Teacher deletion",`Teacher has been deleted.`)))
        ).subscribe(_ => this.router.navigate(["school", this._schoolId, "teachers"]));
      },
      "Teacher deletion",
      "bg-danger text-white"
    );
  }

  changePassword() {
    this.passwordModal.show();
  }

  changeEmail() {
    this.emailModal.show();
  }

  getInitials(student: V3Teacher) {
    return ManagerUtils.getInitials(student.person)
  }

  reloadTeacher(teacher: V3Teacher){
    this.casa.syncTeacher(this._schoolId, teacher.id).subscribe(
      _ => this.toastNotificationService.display(
        new ToastNotification("Refreshing of teacher's eBooks & Exercises", `Teachers' ${this.teacher?.person?.details?.name} eBooks & Exercises has been refreshed successfully. Teacher should be able to access all content.`, "bg-success text-white"))
    )
  }

  resetBookShares(teacher: V3Teacher){
    this.confirmationService.doOnConfirm("Are you sure you to reset teacher eBooks download limit? This will remove the eBooks from all teacher's devices, so that they can download the eBooks to new devices.",
    () => {
      this.casa.clearTeacherDeviceShares(this._schoolId, teacher.id).subscribe(
        _ => this.toastNotificationService.display(
          new ToastNotification("Reset of teacher's eBooks download limit", `Teachers' ${this.teacher?.person?.details?.name} eBooks download limit reset successfully.`)
        )
      )
    }, "Reset of teacher's eBooks download limit","bg-warning");
  }
}
